/*
  This page handles Paypal payment methods after customer approves the transaction/agreement on external Paypal page.
  The customer returns with QueryParameters:
  {
    token: string
  }
  which will be used to:
  1. Create Billing Agreement ID
  2. Fetch customer info from Paypal
  3. And send the customer email + BAID to Zuora for creating a new payment method.
  4. Redirect customer to management page
*/

import { useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  OnPaymentMethodCallbackErrorHandler,
  OnPaymentMethodCallbackSuccessHandler,
  PaymentCallbackError,
  PaymentMethodCallbackPageProps,
} from 'apps-common/components/PaymentCallbackPage';
import {
  CreatePaypalPaymentMethodInput,
  CreatePaypalPaymentMethodResponse,
  paypalPaymentMethod,
} from 'apps-common/graphql/paypalPaymentMethod';
import { useQueryParams } from 'apps-common/hooks/useQueryParams';
import { AppType, BillingPeriod, Currency, FlowType, UserAddressForm } from 'apps-common/types';
import { logger } from 'apps-common/utils/logger';
import { getTranslationLanguage } from 'translations/src/LocaleContext';
import { Loader } from 'ui';

const getPaypalFlowType = (appType: AppType, queryParams: URLSearchParams): FlowType => {
  if (appType === 'signup') {
    return FlowType.Signup;
  }

  return queryParams.get('renew') ? FlowType.Renew : FlowType.AddOrUpdatePaymentMethod;
};

const usePaypalPaymentMethod = () =>
  useMutation<CreatePaypalPaymentMethodResponse['paypalPaymentMethod'], Error, CreatePaypalPaymentMethodInput>({
    mutationKey: ['paypalCallback'],
    mutationFn: async (vars) => {
      const response = await paypalPaymentMethod(vars);
      return response.paypalPaymentMethod;
    },
  });

const paypalCallbackHandler = (
  queryParams: URLSearchParams,
  mutate: ReturnType<typeof usePaypalPaymentMethod>['mutate'],
  appType: AppType,
  onSuccess: OnPaymentMethodCallbackSuccessHandler,
  onError: OnPaymentMethodCallbackErrorHandler,
  setPaymentError: (error: Error) => void,
  billingPeriod: BillingPeriod,
  currency: Currency,
  email: string,
  userAddressForm: UserAddressForm,
  completeRoute: string,
  paymentMethodRoute: string,
  isUnitedStates: boolean,
  paypalPaymentGatewayName: string,
  renewCompleteRoute?: string,
) => {
  const sessionToken = queryParams.get('token');
  const flowType = getPaypalFlowType(appType, queryParams);
  const supportedTranslations = JSON.parse(import.meta.env.VITE_TRANSLATIONS_SUPPORTED) as string[];
  const locale = getTranslationLanguage(supportedTranslations);

  const paymentMethodCallbackTrackingData = {
    locale,
    paymentMethodType: 'Paypal' as const,
    currency,
    billingPeriod,
    paymentGateway: paypalPaymentGatewayName,
  };
  if (!sessionToken) {
    const callbackError = new PaymentCallbackError(
      'Paypal token missing from query parameters',
      'MISSING_PAYPAL_TOKEN',
    );
    onError(paymentMethodRoute, callbackError, appType, paymentMethodCallbackTrackingData);
    return;
  }

  mutate(
    {
      token: sessionToken,
      isUnitedStates,
      paymentGatewayName: paypalPaymentGatewayName,
    },
    {
      onSuccess: (resp) => {
        const successRoute = flowType === FlowType.Renew ? renewCompleteRoute! : completeRoute;
        void onSuccess(
          flowType,
          email,
          userAddressForm,
          resp.paymentMethodId,
          successRoute,
          paymentMethodRoute,
          paymentMethodCallbackTrackingData,
        );
      },
      onError: (error) => {
        const msg = 'Error occurred when creating Paypal payment method';
        logger.error(msg, {
          error,
        });
        const callbackError = new PaymentCallbackError(msg, 'PAYPAL_PAYMENT_METHOD_ERROR');
        setPaymentError(callbackError);
        onError(paymentMethodRoute, callbackError, appType, paymentMethodCallbackTrackingData);
      },
    },
  );
};

const useHandlePaypalCallback = ({
  appType,
  setPaymentError,
  paymentMethodRoute,
  completeRoute,
  renewCompleteRoute,
  billingPeriod,
  currency,
  email,
  userAddressForm,
  isUnitedStates,
  paypalPaymentGatewayName,
  onSuccess,
  onError,
}: PaymentMethodCallbackPageProps) => {
  const queryParams = useQueryParams();
  const { mutate } = usePaypalPaymentMethod();

  useEffect(() => {
    if (!billingPeriod) {
      throw new Error('Billing period is required');
    }
    if (!currency) {
      throw new Error('Currency is required');
    }
    if (!paypalPaymentGatewayName) {
      throw new Error('Paypal payment gateway name is required');
    }
    if (isUnitedStates === undefined) {
      throw new Error('United States status (isUnitedStates) is required');
    }
    paypalCallbackHandler(
      queryParams,
      mutate,
      appType,
      onSuccess,
      onError,
      setPaymentError,
      billingPeriod,
      currency,
      email,
      userAddressForm,
      completeRoute,
      paymentMethodRoute,
      isUnitedStates,
      paypalPaymentGatewayName,
      renewCompleteRoute,
    );
  }, [
    queryParams,
    mutate,
    onSuccess,
    onError,
    appType,
    setPaymentError,
    billingPeriod,
    currency,
    email,
    userAddressForm,
    completeRoute,
    paymentMethodRoute,
    isUnitedStates,
    paypalPaymentGatewayName,
    renewCompleteRoute,
  ]);
};

export const PaypalCallbackPage = (props: PaymentMethodCallbackPageProps) => {
  useHandlePaypalCallback(props);

  return <Loader />;
};
